import { z } from "zod";

// ---- automation journeys (roadmap 9a): ordered steps, wait steps, step retries ----

export const AUTOMATION_TRIGGERS = [
  "gift_received",
  "constituent_created",
  "pledge_created",
  "recurring_cancelled",
  // roadmap 9b
  "tag_added",
  "date_reached",
  "form_submitted",
  "score_changed",
  // CT-RM-26
  "card_expiring",
  "payment_failed",
  // CT-RM-27: a volunteer holds a shift spot (staff/self/form signup, or promoted off the waitlist)
  "volunteer_signed_up",
  // CT-RM-31: a donor started a cart on a donation form (email captured) and didn't check out.
  // Fired in test mode only for now: runs are previewed (dry run), nothing is sent.
  "donation_abandoned",
] as const;
export type AutomationTrigger = (typeof AUTOMATION_TRIGGERS)[number];

/** Constituent dates a date_reached journey can anchor on (evaluated once per day per org). */
export const AUTOMATION_DATE_FIELDS = ["birthday", "first_gift_anniversary", "membership_expiry"] as const;
export type AutomationDateField = (typeof AUTOMATION_DATE_FIELDS)[number];
export const AUTOMATION_DATE_FIELD_LABELS: Record<AutomationDateField, string> = {
  birthday: "birthday",
  first_gift_anniversary: "first-gift anniversary",
  membership_expiry: "membership expiry",
};
export const AUTOMATION_MAX_OFFSET_DAYS = 365;

/** Scores a score_changed journey can watch; written by the scoring recompute. */
export const AUTOMATION_SCORE_FIELDS = ["lapseRisk", "upgradePropensity", "plannedGiftLikelihood", "engagementLevel"] as const;
export type AutomationScoreField = (typeof AUTOMATION_SCORE_FIELDS)[number];
export const AUTOMATION_SCORE_FIELD_LABELS: Record<AutomationScoreField, string> = {
  lapseRisk: "lapse risk",
  upgradePropensity: "upgrade propensity",
  plannedGiftLikelihood: "planned-gift likelihood",
  engagementLevel: "engagement level",
};
export const AUTOMATION_SCORE_DIRECTIONS = ["rises_to", "falls_below"] as const;
export type AutomationScoreDirection = (typeof AUTOMATION_SCORE_DIRECTIONS)[number];
export const AUTOMATION_ACTION_TYPES = ["send_email", "send_sms", "create_task", "add_tag", "update_field", "send_webhook"] as const;
export const AUTOMATION_STEP_TYPES = [...AUTOMATION_ACTION_TYPES, "wait"] as const;
export type AutomationStepType = (typeof AUTOMATION_STEP_TYPES)[number];

export const AUTOMATION_WAIT_UNITS = ["minutes", "hours", "days"] as const;
export type AutomationWaitUnit = (typeof AUTOMATION_WAIT_UNITS)[number];
const WAIT_UNIT_MS: Record<AutomationWaitUnit, number> = { minutes: 60_000, hours: 3_600_000, days: 86_400_000 };
/** Longest single wait step. */
export const AUTOMATION_MAX_WAIT_DAYS = 365;
export const AUTOMATION_MAX_STEPS = 20;
/** A failing step is attempted at most this many times (first try + retries) before the run fails. */
export const AUTOMATION_MAX_STEP_ATTEMPTS = 3;

/** create_task: due date offset when dueInDays is omitted (the engine's long-standing default). */
export const AUTOMATION_DEFAULT_TASK_DUE_DAYS = 3;
export const AUTOMATION_MAX_TASK_DUE_DAYS = 365;

export const zAutomationStep = z
  .object({
    type: z.enum(AUTOMATION_STEP_TYPES),
    // send_email
    subject: z.string().max(300).optional(),
    bodyHtml: z.string().max(50_000).optional(),
    templateId: z.string().uuid().optional(),
    // send_sms
    smsBody: z.string().max(1_600).optional(),
    // create_task
    taskTitle: z.string().max(300).optional(),
    assigneeId: z.string().uuid().optional(),
    dueInDays: z.number().int().min(0).max(AUTOMATION_MAX_TASK_DUE_DAYS).optional(),
    // add_tag
    tag: z.string().max(60).optional(),
    // update_field
    fieldKey: z.string().max(60).optional(),
    fieldValue: z.unknown().optional(),
    // send_webhook
    url: z.string().url().max(500).optional(),
    // wait
    waitAmount: z.number().int().min(1).optional(),
    waitUnit: z.enum(AUTOMATION_WAIT_UNITS).optional(),
  })
  .superRefine((step, ctx) => {
    for (const issue of automationStepIssues(step)) ctx.addIssue({ code: "custom", path: [issue.field], message: issue.message });
  });
export type AutomationStep = z.infer<typeof zAutomationStep>;

export interface AutomationStepIssue {
  /** the step field the issue belongs to (for inline errors) */
  field: keyof AutomationStep;
  message: string;
}

const filled = (s: string | undefined) => (s ?? "").trim().length > 0;
/** Body HTML with visible content: text, or an image. `<p></p>` from an empty editor doesn't count. */
export function automationHtmlHasContent(html: string | undefined): boolean {
  if (!html) return false;
  if (/<img\b/i.test(html)) return true;
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;
}

/**
 * Required-field rules per step type, shared by the editor (inline errors, Create gating) and the
 * API (400 on create/update). Format checks the schema already does (uuid, url, max lengths) aren't repeated.
 */
export function automationStepIssues(step: Partial<AutomationStep> & { type: AutomationStepType }): AutomationStepIssue[] {
  switch (step.type) {
    case "send_email": {
      if (step.templateId) return [];
      const out: AutomationStepIssue[] = [];
      if (!filled(step.subject)) out.push({ field: "subject", message: "Add a subject, or choose a template" });
      if (!automationHtmlHasContent(step.bodyHtml)) out.push({ field: "bodyHtml", message: "Write a message, or choose a template" });
      return out;
    }
    case "send_sms":
      return filled(step.smsBody) ? [] : [{ field: "smsBody", message: "Write the text message" }];
    case "create_task":
      return filled(step.taskTitle) ? [] : [{ field: "taskTitle", message: "Give the task a title" }];
    case "add_tag":
      return filled(step.tag) ? [] : [{ field: "tag", message: "Choose or type a tag" }];
    case "update_field":
      return filled(step.fieldKey) ? [] : [{ field: "fieldKey", message: "Pick a field to update" }];
    case "send_webhook":
      return filled(step.url) ? [] : [{ field: "url", message: "Enter the webhook URL" }];
    case "wait":
      if (step.waitAmount == null || step.waitUnit == null || step.waitAmount < 1) {
        return [{ field: "waitAmount", message: "A wait step needs an amount of at least 1 and a unit" }];
      }
      if (step.waitAmount * WAIT_UNIT_MS[step.waitUnit] > AUTOMATION_MAX_WAIT_DAYS * WAIT_UNIT_MS.days) {
        return [{ field: "waitAmount", message: `A wait step can be at most ${AUTOMATION_MAX_WAIT_DAYS} days` }];
      }
      return [];
    default:
      return [];
  }
}

// ---- merge fields (editor menu + preview) ----

export const AUTOMATION_MERGE_FIELDS = [
  { key: "first_name", token: "{{first_name}}", label: "First name", sample: "Jordan" },
  { key: "last_name", token: "{{last_name}}", label: "Last name", sample: "Rivera" },
  { key: "full_name", token: "{{full_name}}", label: "Full name", sample: "Jordan Rivera" },
  { key: "email", token: "{{email}}", label: "Email", sample: "jordan.rivera@example.org" },
  { key: "last_gift_amount", token: "{{last_gift_amount}}", label: "Last gift amount", sample: "$250.00" },
  { key: "last_gift_date", token: "{{last_gift_date}}", label: "Last gift date", sample: "October 14, 2026" },
  { key: "org_name", token: "{{org_name}}", label: "Organization name", sample: "Riverside Community Trust" },
] as const;
export type AutomationMergeFieldKey = (typeof AUTOMATION_MERGE_FIELDS)[number]["key"];

/** Older journeys use the engine's original tokens; the preview fills them too. */
const LEGACY_MERGE_ALIASES = {
  firstName: "first_name",
  name: "full_name",
  amount: "last_gift_amount",
  date: "last_gift_date",
} as const satisfies Record<string, AutomationMergeFieldKey>;
export type AutomationLegacyMergeToken = keyof typeof LEGACY_MERGE_ALIASES;

const escapeHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export interface RenderAutomationMergeOptions {
  /** escape the values for insertion into HTML */
  html?: boolean;
  /** "keep" (default, editor preview) leaves unknown/unfilled tokens as typed; "empty" removes them (engine) */
  unknown?: "keep" | "empty";
  /**
   * Values for the legacy tokens when they differ from their alias (the engine's `{{amount}}`/`{{date}}`
   * are the triggering event's, not the last gift's). Unset legacy tokens fall back to their alias.
   */
  legacy?: Partial<Record<AutomationLegacyMergeToken, string>>;
}

/**
 * Fills merge tokens (`{{first_name}}`, legacy `{{firstName}}`…). Unknown tokens are left as typed
 * unless `unknown: "empty"`.
 */
export function renderAutomationMergeFields(
  text: string,
  /** the automation fields, plus any feature-specific extras (e.g. membership reminders' `{{expiry_date}}`) */
  values: Partial<Record<AutomationMergeFieldKey | (string & {}), string>> = Object.fromEntries(AUTOMATION_MERGE_FIELDS.map((f) => [f.key, f.sample])),
  opts: RenderAutomationMergeOptions = {},
): string {
  return text.replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, (match, raw: string) => {
    const legacyKey = Object.hasOwn(LEGACY_MERGE_ALIASES, raw) ? (raw as AutomationLegacyMergeToken) : undefined;
    const key: string = legacyKey ? LEGACY_MERGE_ALIASES[legacyKey] : raw;
    const value = (legacyKey ? opts.legacy?.[legacyKey] : undefined) ?? (Object.hasOwn(values, key) ? values[key] : undefined);
    if (value == null) return opts.unknown === "empty" ? "" : match;
    return opts.html ? escapeHtml(value) : value;
  });
}

// ---- SMS length: GSM-7 (160 / 153 per segment) vs UCS-2 (70 / 67) ----

const GSM7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXTENDED = "^{}\\[~]|€\f";

export interface SmsSegmentInfo {
  encoding: "GSM-7" | "UCS-2";
  /** length in the encoding's units (GSM extended characters count twice; UCS-2 counts UTF-16 code units) */
  units: number;
  segments: number;
  /** capacity of each segment at this length: 160/70 for a single segment, 153/67 once split */
  perSegment: number;
}

export function smsSegmentInfo(text: string): SmsSegmentInfo {
  let gsmUnits = 0;
  let gsm = true;
  for (const ch of text) {
    if (GSM7_BASIC.includes(ch)) gsmUnits += 1;
    else if (GSM7_EXTENDED.includes(ch)) gsmUnits += 2;
    else {
      gsm = false;
      break;
    }
  }
  const [units, single, multi] = gsm ? [gsmUnits, 160, 153] : [text.length, 70, 67];
  if (units === 0) return { encoding: gsm ? "GSM-7" : "UCS-2", units: 0, segments: 0, perSegment: single };
  const split = units > single;
  return { encoding: gsm ? "GSM-7" : "UCS-2", units, segments: split ? Math.ceil(units / multi) : 1, perSegment: split ? multi : single };
}

export const zAutomationSteps = z.array(zAutomationStep).min(1).max(AUTOMATION_MAX_STEPS);

export const zAutomationConditions = z.object({
  minAmount: z.number().int().min(0).optional(),
  maxAmount: z.number().int().min(0).optional(),
  fundId: z.string().uuid().optional(),
  channel: z.string().max(30).optional(),
  firstGiftOnly: z.boolean().optional(),
  // tag_added: only this tag (any tag when omitted; matched case-insensitively)
  tag: z.string().trim().max(60).optional(),
  // date_reached: fire offsetDays before/after the constituent's date (0 = on the day)
  dateField: z.enum(AUTOMATION_DATE_FIELDS).optional(),
  offsetDays: z.number().int().min(0).max(AUTOMATION_MAX_OFFSET_DAYS).optional(),
  offsetDirection: z.enum(["before", "after"]).optional(),
  // form_submitted / donation_abandoned: only this landing page's form (any form when omitted)
  pageId: z.string().uuid().optional(),
  // volunteer_signed_up: only this opportunity's shifts (any opportunity when omitted)
  opportunityId: z.string().uuid().optional(),
  // score_changed: fire when the score crosses the threshold in this direction
  scoreField: z.enum(AUTOMATION_SCORE_FIELDS).optional(),
  scoreDirection: z.enum(AUTOMATION_SCORE_DIRECTIONS).optional(),
  threshold: z.number().int().min(0).max(100).optional(),
});
export type AutomationConditions = z.infer<typeof zAutomationConditions>;

/** Trigger-specific required conditions; null when the pair is valid. */
export function automationTriggerIssue(trigger: AutomationTrigger, cond: AutomationConditions): string | null {
  if (trigger === "date_reached" && !cond.dateField) return "A date trigger needs a date field";
  if (trigger === "score_changed" && (!cond.scoreField || cond.threshold == null)) return "A score trigger needs a score and a threshold";
  return null;
}

/**
 * score_changed: did the score cross the threshold? "rises_to" = was below (or unscored), now at/above;
 * "falls_below" = was at/above, now below. Unscored afterwards never counts as falling.
 */
export function automationScoreCrossed(cond: AutomationConditions, before: number | null, after: number | null): boolean {
  if (!cond.scoreField || cond.threshold == null) return false;
  const t = cond.threshold;
  if ((cond.scoreDirection ?? "rises_to") === "rises_to") return after != null && after >= t && (before == null || before < t);
  return after != null && after < t && before != null && before >= t;
}

/** Days from the evaluation date to the anchor occurrence: +N before, -N after. */
export function automationDateShiftDays(cond: AutomationConditions): number {
  const n = cond.offsetDays ?? 0;
  return cond.offsetDirection === "after" ? -n : n;
}

export function describeAutomationTrigger(a: { trigger: AutomationTrigger; conditions: AutomationConditions }): string {
  const c = a.conditions;
  switch (a.trigger) {
    case "tag_added":
      return c.tag ? `tag "${c.tag}" added` : "any tag added";
    case "date_reached": {
      const field = c.dateField ? AUTOMATION_DATE_FIELD_LABELS[c.dateField] : "date";
      const n = c.offsetDays ?? 0;
      return n === 0 ? `on ${field}` : `${n} day${n === 1 ? "" : "s"} ${c.offsetDirection ?? "before"} ${field}`;
    }
    case "form_submitted":
      return c.pageId ? "form submitted (one page)" : "any form submitted";
    case "donation_abandoned":
      return c.pageId ? "donation abandoned (one form)" : "donation abandoned (any form)";
    case "volunteer_signed_up":
      return c.opportunityId ? "volunteer signed up (one opportunity)" : "volunteer signed up (any opportunity)";
    case "score_changed": {
      const field = c.scoreField ? AUTOMATION_SCORE_FIELD_LABELS[c.scoreField] : "score";
      return `${field} ${(c.scoreDirection ?? "rises_to") === "rises_to" ? "rises to" : "falls below"} ${c.threshold ?? "?"}`;
    }
    default:
      return a.trigger.replace(/_/g, " ");
  }
}

/**
 * Create/update body. `steps` is the ordered journey; `actions` is the legacy name for the
 * same list and is still accepted (steps wins when both are sent).
 */
export const zUpsertAutomation = z.object({
  name: z.string().trim().min(1).max(200),
  trigger: z.enum(AUTOMATION_TRIGGERS),
  conditions: zAutomationConditions.default({}),
  steps: zAutomationSteps.optional(),
  actions: zAutomationSteps.optional(),
  enabled: z.boolean().default(true),
});
export const zCreateAutomation = zUpsertAutomation
  .refine((b) => b.steps != null || b.actions != null, {
    message: "An automation needs at least one step",
    path: ["steps"],
  })
  .superRefine((b, ctx) => {
    const issue = automationTriggerIssue(b.trigger, b.conditions);
    if (issue) ctx.addIssue({ code: "custom", path: ["conditions"], message: issue });
  });
export const zUpdateAutomation = zUpsertAutomation.partial();
export type CreateAutomationInput = z.input<typeof zCreateAutomation>;

/** Duration of a wait step in ms (0 for non-wait steps). */
export function automationWaitMs(step: Pick<AutomationStep, "type" | "waitAmount" | "waitUnit">): number {
  if (step.type !== "wait" || step.waitAmount == null || step.waitUnit == null) return 0;
  return step.waitAmount * WAIT_UNIT_MS[step.waitUnit];
}

/** Backoff before the next attempt after `attempts` failed tries: 1 min, then 5 min. */
export function automationRetryBackoffMs(attempts: number): number {
  return 60_000 * 5 ** Math.max(0, attempts - 1);
}

/**
 * Reads both stored shapes: the ordered array, or a legacy single action object
 * (migration 0040 wraps those, this keeps any stragglers working).
 */
export function normalizeAutomationSteps(raw: unknown): AutomationStep[] {
  if (Array.isArray(raw)) return raw as AutomationStep[];
  if (raw && typeof raw === "object" && "type" in raw) return [raw as AutomationStep];
  return [];
}

export function describeAutomationStep(step: AutomationStep): string {
  if (step.type === "wait") return `wait ${step.waitAmount ?? "?"} ${step.waitUnit ?? ""}`.trim();  return step.type.replace(/_/g, " ");
}

export const AUTOMATION_RUN_STATUSES = ["running", "waiting", "retrying", "completed", "skipped", "failed"] as const;
export type AutomationRunStatus = (typeof AUTOMATION_RUN_STATUSES)[number];
/** Runs the scheduler picks up once resume_at has passed. */
export const AUTOMATION_RESUMABLE_STATUSES: readonly AutomationRunStatus[] = ["waiting", "retrying"];

export const AUTOMATION_STEP_STATUSES = ["pending", "running", "waiting", "retrying", "completed", "failed", "skipped"] as const;
export type AutomationStepStatus = (typeof AUTOMATION_STEP_STATUSES)[number];

export interface AutomationStepResult {
  index: number;
  type: AutomationStepType;
  status: AutomationStepStatus;
  attempts: number;
  detail: string | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  /** when a waiting/retrying step will next run */
  resumeAt: string | null;
}

// ---- test mode (roadmap 9b): a dry run for one constituent; nothing is sent or written ----

export const zTestAutomation = z.object({ constituentId: z.string().uuid() });
export type TestAutomationInput = z.infer<typeof zTestAutomation>;

/** would_run: the step would act; would_skip: a guard (consent, missing data) would stop it; wait_skipped: waits don't wait in test mode. */
export const AUTOMATION_TEST_OUTCOMES = ["would_run", "would_skip", "wait_skipped"] as const;
export type AutomationTestOutcome = (typeof AUTOMATION_TEST_OUTCOMES)[number];

export interface AutomationTestStep {
  index: number;
  type: AutomationStepType;
  outcome: AutomationTestOutcome;
  detail: string;
}

export interface AutomationTestResult {
  automationId: string;
  constituentId: string;
  constituentName: string;
  trigger: AutomationTrigger;
  /** what the trigger would see for this constituent (sample gift, current score, next date…) */
  triggerNote: string;
  /** whether the automation's conditions match the sample; steps are previewed either way */
  conditionsMatched: boolean;
  conditionsDetail: string;
  steps: AutomationTestStep[];
  /** always true: test mode never enqueues, calls webhooks, or writes tags/tasks/fields */
  dryRun: true;
}

export interface AutomationRow {
  id: string;
  name: string;
  trigger: AutomationTrigger;
  conditions: AutomationConditions;
  steps: AutomationStep[];
  /** @deprecated same list as `steps` */
  actions: AutomationStep[];
  enabled: boolean;
  runCount: number;
  lastRunAt: string | null;
  createdAt: string;
}

export interface AutomationRunRow {
  id: string;
  constituentId: string | null;
  constituentName: string | null;
  status: AutomationRunStatus;
  currentStep: number;
  resumeAt: string | null;
  steps: AutomationStepResult[];
  log: { step: string; detail: string }[];
  createdAt: string;
  updatedAt: string;
}
